import { getErrorCode } from '@pexip/infinity-api';
import { NONE } from './constants';
import { fetchWithInfoLog } from './fetcher';
import { logger } from './logger';
import { createRequestClient } from './requestClient';
import { buildSsoTargetUrl } from './utils';
const getDefaultProps = () => ({
    pin: undefined,
    ssoToken: NONE,
    token: undefined,
    conferenceExtension: undefined,
    callTag: undefined,
    chosenIdp: NONE,
    conferenceName: undefined,
    requestClient: undefined,
});
export const createAuth = ({ API, signals, cleanupAndDisconnect, }) => {
    const props = getDefaultProps();
    const cleanup = () => {
        const { requestClient: _, ...defaultProps } = getDefaultProps();
        Object.assign(props, defaultProps);
    };
    const emitErrorSignal = (error) => {
        signals.onError.emit({
            error: error,
            errorCode: getErrorCode(error),
        });
    };
    const releaseToken = async (reason) => {
        await props.requestClient?.cleanup(reason);
        props.requestClient = undefined;
    };
    const requestToken = async (opts) => {
        const pin = opts.pin ?? props.pin;
        const chosenIdp = opts.chosenIdp ?? props.chosenIdp;
        const ssoToken = opts.ssoToken ?? props.ssoToken;
        const token = opts.token ?? props.token;
        const conferenceExtension = opts.conferenceExtension ?? props.conferenceExtension;
        const callTag = opts.callTag ?? props.callTag;
        const conferenceAlias = opts.conferenceAlias;
        const displayName = opts.displayName;
        const node = opts.node;
        const host = opts.host;
        let ssoRedirectTarget;
        if (conferenceAlias) {
            logger.addRedactPattern(conferenceAlias);
            // The alias is logged encoded: percent-encoded in request paths and
            // form-encoded (`+` for a space) in the SSO redirect target.
            // Neither is matched by the raw pattern, or by each other.
            const pathEncodedAlias = encodeURIComponent(conferenceAlias);
            const formEncodedAlias = new URLSearchParams([
                ['', conferenceAlias],
            ])
                .toString()
                .slice(1);
            new Set([pathEncodedAlias, formEncodedAlias]).forEach(encodedAlias => {
                if (encodedAlias !== conferenceAlias) {
                    logger.addRedactPattern(encodedAlias);
                }
            });
            ssoRedirectTarget = buildSsoTargetUrl({
                conferenceAlias,
                displayName,
                callTag,
                callType: opts.callType,
                isMeetingManager: opts.isMeetingManager,
            });
        }
        const res = await API.requestToken({
            fetcher: pin
                ? API.withPin(fetchWithInfoLog, pin)
                : fetchWithInfoLog,
            body: {
                display_name: displayName,
                chosen_idp: chosenIdp,
                direct_media: opts.directMedia,
                sso_token: ssoToken,
                sso_redirect_target: ssoRedirectTarget,
                node,
                token,
                registration_token: opts.registrationToken,
                conference_extension: conferenceExtension,
                call_tag: callTag,
                client_id: opts.clientId,
                supports_direct_chat: opts.supportsDirectChat,
            },
            params: {
                conferenceAlias,
            },
            host,
        });
        switch (res.status) {
            case 200:
                if (pin) {
                    props.pin = pin;
                }
                if (chosenIdp) {
                    props.chosenIdp = chosenIdp;
                }
                if (ssoToken) {
                    props.ssoToken = ssoToken;
                }
                if (conferenceExtension) {
                    props.conferenceExtension = conferenceExtension;
                }
                if (callTag) {
                    props.callTag = callTag;
                }
                props.conferenceName = res.data.result.conference_name;
                props.requestClient = createRequestClient({
                    fetcher: fetchWithInfoLog,
                    token: res.data.result.token,
                    expires: Number(res.data.result.expires),
                    clientStatsUpdateInterval: res.data.result.client_stats_update_interval,
                    tokenExpiredCb: () => {
                        signals.onTokenExpired.emit('Timer expired awaiting token refresh');
                        cleanupAndDisconnect('Timer expired awaiting token refresh');
                    },
                    tokenRefreshedCb: token => {
                        signals.onTokenRefreshed.emit({ token });
                    },
                    refreshTokenFn: fetcher => API.refreshToken({
                        fetcher,
                        params: { conferenceAlias },
                        host,
                    }).then(res => (res.status === 200 ? res : undefined)),
                    releaseTokenFn: (fetcher, body) => API.releaseToken({
                        fetcher,
                        params: { conferenceAlias },
                        body,
                        host,
                    }).then(() => undefined),
                    releaseTokenBeaconUrl: (token) => `${host}/api/client/v2/conferences/${conferenceAlias}/release_token?token=${token}`,
                });
                break;
            case 403:
            case 415:
                // For this response string is likely considered as error
                if (typeof res.data.result === 'string') {
                    emitErrorSignal(res.data.result);
                }
                else if ('pin' in res.data.result &&
                    'guest_pin' in res.data.result) {
                    if (conferenceExtension) {
                        props.conferenceExtension = conferenceExtension;
                    }
                    signals.onPinRequired.emit({
                        hasHostPin: res.data.result.pin === 'required',
                        hasGuestPin: res.data.result.guest_pin === 'required',
                    });
                }
                else if ('idp' in res.data.result) {
                    if (pin) {
                        props.pin = pin;
                    }
                    const idps = res.data.result.idp.map(({ disable_popup_flow, ...rest }) => ({
                        disablePopupFlow: disable_popup_flow,
                        ...rest,
                    }));
                    signals.onIdp.emit(idps);
                }
                else if ('redirect_url' in res.data.result &&
                    'redirect_idp' in res.data.result) {
                    signals.onRedirect.emit({
                        redirectUrl: res.data.result.redirect_url,
                        redirectIdp: res.data.result.redirect_idp,
                        disablePopupFlow: res.data.result.disable_popup_flow,
                    });
                }
                else if ('conference_extension' in res.data.result &&
                    'conference_extension_type' in res.data.result) {
                    signals.onExtension.emit(res.data.result.conference_extension_type);
                }
                else if (res.data.result.display_name === 'required') {
                    if (pin) {
                        props.pin = pin;
                    }
                    signals.onDisplayNameRequired.emit();
                }
                break;
            case 404:
                emitErrorSignal(res.data.result);
                break;
            case 502:
            case 503:
            case 504:
            case 529:
                emitErrorSignal(res.data.result);
                break;
        }
        return res;
    };
    const setCallTag = (callTag) => {
        props.callTag = callTag;
    };
    const setConferenceExtension = (conferenceExtension) => {
        props.conferenceExtension = conferenceExtension;
    };
    const setPin = (pin = NONE) => {
        props.pin = pin;
    };
    const setSsoToken = (ssoToken = NONE) => {
        props.ssoToken = ssoToken;
    };
    const setToken = (token) => {
        props.token = token;
    };
    return {
        get callTag() {
            return props.callTag;
        },
        get conferenceName() {
            return props.conferenceName;
        },
        get requestClient() {
            return props.requestClient;
        },
        cleanup,
        releaseToken,
        requestToken,
        setCallTag,
        setConferenceExtension,
        setPin,
        setSsoToken,
        setToken,
    };
};
//# sourceMappingURL=auth.js.map